import { GridReadyEvent } from "@ag-grid-community/core";
import { Component, Optional, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ActiveStatusEnum,
  ItemBin,
  ItemDetail,
  ItemDetailStatus,
  ItemDetailStatusEnum,
  Manufacturer,
  Product,
  ProductType,
} from "app/model/entities/entity-model";

import { AgFns, GridOptionsExt, ISortModel } from "app/shared/ag-fns";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFinderDialog } from "app/shared/entity-finder.dialog";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { ManufacturerFinderDialogComponent } from "./manufacturer-finder-dialog.component";
import { DialogBaseComponent } from "@/shared/dialog-base.component";
import { ItemDetailLogDialogComponent } from "@/shared/item-detail-log-dialog.component";
import { InventoryManagerComponent } from "./inventory-manager.component";

interface StatusChoice {
  id: number;
  name: string;
}

@Component({
  selector: "app-inventory-reconcile",
  templateUrl: "./inventory-reconcile.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InventoryReconcileComponent extends DomainBaseComponent {
  @ViewChild("selectItemDetailCell") selectItemDetailCell: TemplateRef<any>;
  @ViewChild("viewLogCell") viewLogCell: TemplateRef<any>;

  itdGridOptions: GridOptionsExt;

  allManufs: Manufacturer[];
  searchProductTypes: ProductType[];
  selectedManuf: Manufacturer;
  selectedProductType: ProductType;
  productTypeId: number;

  editableStatuses: ItemDetailStatus[];
  statusChoices: StatusChoice[];
  selectedStatusIds: number[] = [ItemDetailStatusEnum.InInventory];

  editableStatusCodes = [
    ItemDetailStatusEnum.InInventory,
    ItemDetailStatusEnum.DamagedOrOther,
    ItemDetailStatusEnum.MissingFromInventory,
    ItemDetailStatusEnum.Special,
  ];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    @Optional() private inventoryManager: InventoryManagerComponent,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    if (this.inventoryManager) {
      this.inventoryManager.setScannerSearchFn(this.scannerSearchFn);
      this.inventoryManager.scannerSubmitted$
        .pipe(takeUntil(this.onDestroy))
        .subscribe((id) => this.onScannerResult(id));

      setTimeout(() => {
        this.inventoryManager.showEoqButtons = false;
        this.inventoryManager.showAddInventoryButtons = false;
        this.inventoryManager.showPrintBarcodesButton = false;
        this.inventoryManager.showScannerControls = true;
      });
    }
  }

  override ngOnDestroy() {
    if (this.inventoryManager) {
      this.inventoryManager.showScannerControls = false;
      this.inventoryManager.scannerDisabled = false;
    }
    super.ngOnDestroy();
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(ItemDetail);
    this.uow.clearEntities(ProductType);
    this.uow.clearEntities(Product);
    return true;
  }

  async updateFromContext() {
    this.productTypeId = +this.route.snapshot.params["productTypeId"];

    this.editableStatuses = this.dbQueryService.getAllCached(ItemDetailStatus);
    this.editableStatuses = this.editableStatuses.filter((s) => this.editableStatusCodes.some((cd) => cd === s.id));

    this.statusChoices = this.editableStatusCodes.map((cd) => {
      const itds = this.editableStatuses.find((s) => s.id === cd);
      return { id: cd, name: this.shortStatusLabel(cd, itds?.name) };
    });

    const allManufs = await this.uow.queryAll(Manufacturer, "Manufacturers");
    await this.uow.queryAll(ItemBin, "ItemBins");
    this.allManufs = _.sortBy(allManufs, (m) => m.name);

    this.canEditStatus = this.canEditStatus.bind(this);
    this.getEditableClass = this.getEditableClass.bind(this);

    this.itdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItdGridReady,
      getRowStyle: this.getItdRowStyle,
      onCellValueChanged: this.onItdCellValueChanged,
      getRowId: (p) => p.data?.id,
      onFilterChanged: () => this.updateLocation(),
      onSortChanged: () => this.updateLocation(),
      rowModelType: "serverSide",
      onPaginationChanged: (p) => {
        if (p.newPage) this.updateLocation();
      },
      overlayNoRowsTemplate: "Please select a manufacturer and product type, or scan an inventory ID",
    });
    AgFns.captureGridRouteParams(this.itdGridOptions, this.route, "itdKey");

    this.isPageReady = true;
    this.focusScanner();
  }

  private shortStatusLabel(id: ItemDetailStatusEnum, fallback?: string): string {
    switch (id) {
      case ItemDetailStatusEnum.InInventory: return "In Inventory";
      case ItemDetailStatusEnum.MissingFromInventory: return "Missing";
      case ItemDetailStatusEnum.DamagedOrOther: return "Damaged";
      case ItemDetailStatusEnum.Special: return "Special";
      default: return fallback ?? String(id);
    }
  }

  // --- Scanner ---

  scannerSearchFn = async (itemDetailId: string): Promise<string | null> => {
    const itd = await this.dbQueryService.getItemDetailById(itemDetailId);
    if (itd == null) {
      throw new Error("No matching inventory found");
    }
    for (const pfc of itd.product.productFeatureChoices) {
      if (pfc.featureChoice.activeStatusId !== ActiveStatusEnum.Active) {
        throw new Error("Feature Choice status must be 'Active' to be scanned");
      }
    }
    return itemDetailId;
  };

  async onScannerResult(itemDetailId: string) {
    const itd = await this.dbQueryService.getItemDetailById(itemDetailId);
    if (!itd) return;

    itd.lastScanTs = new Date(Date.now());
    await this.saveChanges();
    this.itdGridOptions.apiX?.refreshCells();

    await this.selectProductType(itd.product.productType);
    this.focusScanner();
  }

  focusScanner() {
    this.inventoryManager?.clearAndFocusScanner();
  }

  // --- Grid ---

  getItdRowStyle(params) {
    if (params.node.rowPinned) {
      return { "font-weight": "bold" };
    }
  }

  private onItdGridReady(event: GridReadyEvent) {
    let colDefs = [
      { headerName: "Inventory Identifier", field: "id" },
      { headerName: "Description", field: "product.productType.description" },
      { headerName: "Features", field: "product.featureChoicesExtract" },
      { headerName: "Status", field: "itemDetailStatus.name" },
      {
        headerName: "Update Status",
        field: "updateStatus",
        filter: "agTextColumnFilter",
        editable: this.canEditStatus,
        cellClass: this.getEditableClass,
        ...AgFns.createDropdownEditorPropsFromArray(this.editableStatuses, "id", "name"),
      },
      { headerName: "Last Scanned", field: "lastScanTs" },
      { headerName: "Bin", field: "itemBin.name" },
      {
        ...AgFns.createCheckboxProps("Account-Owned", "isAccountOwned"),
        editable: false,
      },
      { ...AgFns.createCellButtonProps("Mark Found", this.selectItemDetailCell) },
      { ...AgFns.createCellButtonProps("View Log", this.viewLogCell) },
    ];

    if (!this.user.isInventoryAdmin) {
      colDefs = colDefs.filter((cd) => cd.headerName !== "Update Status" && cd.headerName !== "Mark Found");
    }

    const sortFields = ["product.productType.description", "product.choiceValue"];
    const sortModel = sortFields.map((sf) => ({ colId: sf, sort: "asc" })) as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateDatasource();
    AgFns.applyGridRouteParams(this.itdGridOptions);
  }

  private updateLocation(key: any = null) {
    const currentPath = this.domainService.location.path().split("?")[0];
    const urlTree = this.router.createUrlTree([currentPath]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.itdGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  canEditStatus(params: any) {
    return this.isInEditableStatus(<ItemDetail>params.node.data);
  }

  getEditableClass(params: any) {
    return this.isInEditableStatus(<ItemDetail>params.node.data) ? "cell-editable" : "";
  }

  isInEditableStatus(itd: ItemDetail) {
    return this.editableStatusCodes.some((cd) => cd === itd.itemDetailStatusId);
  }

  async onItdCellValueChanged(event: any) {
    const itd = <ItemDetail>event.node.data;
    if (event?.column?.colId === "updateStatus") {
      if (event.newValue !== itd.itemDetailStatusId) {
        itd.itemDetailStatusId = event.newValue;
        this.itdGridOptions.apiX.refreshCells();
      }
      await this.saveChanges();
    }
    this.focusScanner();
  }

  // --- Toolbar actions ---

  async selectManufacturer() {
    const manufs = await EntityFinderDialog.open(ManufacturerFinderDialogComponent, this);
    if (!manufs || manufs.length === 0) {
      this.focusScanner();
      return;
    }
    this.selectedManuf = manufs[0];
    this.searchProductTypes = await this.dbQueryService.getProductTypesForManuf(this.selectedManuf.id);
    this.selectedProductType = null;
    this.productTypeId = null;
    this.updateDatasource();
    this.focusScanner();
  }

  onProductTypeSelected() {
    if (!this.selectedProductType) {
      this.productTypeId = null;
      this.updateDatasource();
      this.focusScanner();
      return;
    }
    this.productTypeId = this.selectedProductType.id;
    this.updateDatasource();
    this.focusScanner();
  }

  onSelectedStatusIdsChange(ids: number[]) {
    // Don't allow an empty selection — fall back to InInventory.
    if (!ids || ids.length === 0) {
      this.selectedStatusIds = [ItemDetailStatusEnum.InInventory];
    } else {
      this.selectedStatusIds = ids;
    }
    this.updateDatasource();
    this.focusScanner();
  }

  private updateDatasource() {
    const gridApi = this.itdGridOptions?.apiX;
    if (gridApi == null) return;

    if (!this.productTypeId) {
      gridApi.setGridOption("overlayNoRowsTemplate", "Please select a manufacturer and product type, or scan an inventory ID");
      gridApi.setGridOption("serverSideDatasource", null);
      gridApi.showNoRowsOverlay();
      return;
    }

    gridApi.setGridOption("overlayNoRowsTemplate", "No inventory items found for selected product type");

    const ds = AgFns.buildDatasource(() =>
      this.dbQueryService.createItemDetailQuery(this.productTypeId, this.selectedStatusIds),
    );
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  // --- Per-row actions ---

  async viewLog(itemDetail: ItemDetail) {
    await DialogBaseComponent.open(ItemDetailLogDialogComponent, this, { itemDetail });
    this.focusScanner();
  }

  async selectItemDetail(itemDetail: ItemDetail) {
    itemDetail.lastScanTs = new Date(Date.now());
    await this.saveChanges();
    this.itdGridOptions.apiX?.refreshCells();
    await this.selectProductType(itemDetail.product.productType);
    this.focusScanner();
  }

  private async selectProductType(productType: ProductType) {
    if (productType.id !== this.productTypeId) {
      this.productTypeId = productType.id;
      if (this.selectedManuf !== productType.manufacturer) {
        this.selectedManuf = productType.manufacturer;
        this.searchProductTypes = await this.dbQueryService.getProductTypesForManuf(this.selectedManuf.id);
      }
      this.selectedProductType = productType;
    }
    this.updateDatasource();
  }

  async saveChanges() {
    await AgFns.busyGrid(this.itdGridOptions, this, async () => {
      await this.dbSaveService.saveChanges();
    });
  }
}
